import {
  CreateDayScheduleItemRequest,
  DayScheduleItemDto,
  FeatureScheduleDto,
} from "../interfaces/control-feature";
import { getBrowserTimezone, isValidTimezone } from "./timezoneUtils";

export const DAYS_OF_WEEK = [
  { value: 0, labelKey: "sun" },
  { value: 1, labelKey: "mon" },
  { value: 2, labelKey: "tue" },
  { value: 3, labelKey: "wed" },
  { value: 4, labelKey: "thu" },
  { value: 5, labelKey: "fri" },
  { value: 6, labelKey: "sat" },
];

export type AvailabilityMode = "all_day" | "same_time" | "per_day";

export type PerDayRow = {
  dayOfWeek: number;
  enabled: boolean;
  allDay: boolean;
  startTime: string;
  endTime: string;
};

export const defaultFeatureSchedule = (): FeatureScheduleDto => ({
  allDay: true,
  daysOfWeek: [],
  startTime: "",
  endTime: "",
  timezone: getBrowserTimezone(),
  daySchedules: undefined,
});

export const defaultDaySchedules = (): PerDayRow[] =>
  [0, 1, 2, 3, 4, 5, 6].map((dayOfWeek) => ({
    dayOfWeek,
    enabled: false,
    allDay: true,
    startTime: "",
    endTime: "",
  }));

export const resolveAvailabilityMode = (
  schedule?: FeatureScheduleDto,
): AvailabilityMode => {
  if (schedule?.daySchedules && schedule.daySchedules.length > 0) {
    return "per_day";
  }
  if (schedule && !schedule.allDay) {
    return "same_time";
  }
  return "all_day";
};

export const perDayRowsFromSchedule = (
  schedule?: FeatureScheduleDto,
): PerDayRow[] => {
  const rows = defaultDaySchedules();
  schedule?.daySchedules?.forEach((daySchedule: DayScheduleItemDto) => {
    const index = daySchedule.dayOfWeek;
    if (index >= 0 && index <= 6) {
      rows[index] = {
        dayOfWeek: index,
        enabled: true,
        allDay: daySchedule.allDay,
        startTime: daySchedule.startTime ?? "",
        endTime: daySchedule.endTime ?? "",
      };
    }
  });
  return rows;
};

export const buildFeatureSchedulePayload = ({
  availabilityMode,
  schedule,
  perDayRows,
}: {
  availabilityMode: AvailabilityMode;
  schedule: FeatureScheduleDto;
  perDayRows: PerDayRow[];
}): FeatureScheduleDto => {
  const scheduleTimezone =
    schedule.timezone || getBrowserTimezone();

  if (availabilityMode === "per_day") {
    const enabled = perDayRows.filter((row) => row.enabled);
    return {
      allDay: false,
      timezone: scheduleTimezone,
      daySchedules: enabled.map((row) =>
        row.allDay
          ? { dayOfWeek: row.dayOfWeek, allDay: true, timezone: scheduleTimezone }
          : {
              dayOfWeek: row.dayOfWeek,
              allDay: false,
              startTime: row.startTime,
              endTime: row.endTime,
              timezone: scheduleTimezone,
            },
      ),
    };
  }

  const normalizedDaysOfWeek =
    (schedule.daysOfWeek?.length ?? 0) > 0
      ? schedule.daysOfWeek
      : DAYS_OF_WEEK.map((day) => day.value);

  return {
    allDay: schedule.allDay,
    timezone: scheduleTimezone,
    daysOfWeek: normalizedDaysOfWeek,
    startTime: schedule.allDay ? undefined : schedule.startTime || undefined,
    endTime: schedule.allDay ? undefined : schedule.endTime || undefined,
  };
};

export const validateFeatureSchedule = (
  availabilityMode: AvailabilityMode,
  schedule: FeatureScheduleDto,
  perDayRows: PerDayRow[],
): string | null => {
  const scheduleTimezone = schedule.timezone || getBrowserTimezone();
  if (!isValidTimezone(scheduleTimezone)) {
    return "invalidTimezone";
  }

  if (availabilityMode === "per_day") {
    const enabled = perDayRows.filter((row) => row.enabled);
    if (enabled.length === 0) {
      return "selectAtLeastOneDay";
    }

    const invalid = enabled.find(
      (row) =>
        !row.allDay &&
        (!row.startTime ||
          !row.endTime ||
          (row.startTime && row.endTime && row.endTime <= row.startTime)),
    );

    if (invalid) {
      return "endTimeMustBeAfterStartTime";
    }

    return null;
  }

  if (!schedule.allDay) {
    if (!schedule.startTime || !schedule.endTime) {
      return "isNotEmpty";
    }
    if (schedule.endTime <= schedule.startTime) {
      return "endTimeMustBeAfterStartTime";
    }
  }

  return null;
};

export type { CreateDayScheduleItemRequest };
